import { FirestoreRepository } from '../interfaces/FirestoreRepository';

/**
 * Pattern A — "a cleared optional field must actually clear" — in ONE place.
 *
 * THE CONTRACT
 * ---------------------------------------------------------------------------
 * A management modal that lets the user blank an optional field sends `null`
 * for it, never an omitted key. `null` is a WIRE-LEVEL signal meaning "the user
 * cleared this"; every model class collapses `null → undefined` in its
 * constructor, so a hydrated model never holds null and read sites stay
 * `T | undefined`.
 *
 * That one wire signal needs TWO different translations, and getting either
 * wrong is silent:
 *
 *  • UPDATE — `updateById` is a MERGE and `removeUndefinedValues` only strips
 *    `undefined`, so an omitted key leaves the stored value in place. The null
 *    must become an explicit Firestore `deleteField()` tombstone or the clear
 *    no-ops behind a success toast (a "cleared" group price kept charging on
 *    the POS, a cleared discount expiry stayed enforced). → {@link
 *    ClearableFieldSet.tombstoneOnUpdate}
 *
 *  • CREATE — a brand-new document has nothing to clear, so the same null just
 *    means "no value". Persisting it literally writes `field: null` onto a
 *    fresh document, which is neither absent nor a tombstone: reads then have
 *    to defend against a third state that the model types say cannot exist.
 *    The key is dropped instead. → {@link ClearableFieldSet.dropOnCreate}
 *
 * Each service declares ONLY its field list and calls these two methods; the
 * transformation itself lives here so the two halves can never drift apart.
 * The list is checked against `keyof TModel` at compile time, so a renamed or
 * misspelled field is a type error rather than a silently un-clearable field.
 *
 * WHY THIS PACKAGE: the tombstone sentinel is `FirestoreRepository.deleteField()`,
 * which lives in this package (`@order-up/client` owns the `firebase` dependency
 * — models/schemas/common must not). Every call site is a client service.
 *
 * NOT COVERED — `shiftService.updateShift` uses an older, DIFFERENT wire signal:
 * an explicit `undefined` means "clear" there (`ShiftModal` always sets the key
 * and `ShiftSchema` never widened to nullable). It is internally consistent
 * (an undefined key is correctly absent on create) and pinned by its own test,
 * so it deliberately stays off this helper. New clearable fields must use the
 * `null` signal and this helper — do not copy the shifts pattern.
 */
export interface ClearableFieldSet<TModel> {
  /** The declared clearable field names, for tests and cross-surface parity checks. */
  readonly fields: readonly (keyof TModel & string)[];

  /**
   * UPDATE path: turn every cleared (`null`) field into a Firestore
   * `deleteField()` tombstone so a MERGE update actually removes it.
   * Mutates and returns the same payload object.
   */
  tombstoneOnUpdate<TPayload extends object>(payload: TPayload): TPayload;

  /**
   * CREATE path: drop every cleared (`null`) field so no literal null lands on
   * a brand-new document. Mutates and returns the same payload object.
   */
  dropOnCreate<TPayload extends object>(payload: TPayload): TPayload;
}

/**
 * Declares the optional fields of `TModel` that a UI can clear, and returns the
 * two payload transformations that implement the clear (see
 * {@link ClearableFieldSet} for the contract).
 *
 * @example
 * const ITEM_CLEARABLE_FIELDS = clearableFields<IItem>(['plu', 'sku']);
 * // create: ITEM_CLEARABLE_FIELDS.dropOnCreate(payload)
 * // update: ITEM_CLEARABLE_FIELDS.tombstoneOnUpdate(payload)
 */
export const clearableFields = <TModel>(
  fields: readonly (keyof TModel & string)[],
): ClearableFieldSet<TModel> => ({
  fields,

  tombstoneOnUpdate<TPayload extends object>(payload: TPayload): TPayload {
    const target = payload as Record<string, unknown>;
    for (const field of fields) {
      if (target[field] === null) {
        target[field] = FirestoreRepository.deleteField();
      }
    }
    return payload;
  },

  dropOnCreate<TPayload extends object>(payload: TPayload): TPayload {
    const target = payload as Record<string, unknown>;
    for (const field of fields) {
      if (target[field] === null) {
        delete target[field];
      }
    }
    return payload;
  },
});
